import React, { useMemo } from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { Badge } from '../ui/Badge';
import { Button } from '../ui/Button';
import { clsx } from 'clsx';
import { differenceInDays, format } from 'date-fns';
import { motion, AnimatePresence } from 'framer-motion';

/**
 * Maps assignee role types to their corresponding Feather icons.
 */
const assigneeIcons = {
  minister:            FiIcons.FiStar,
  permanent_secretary: FiIcons.FiShield,
  executive_director:  FiIcons.FiUser,
  director:            FiIcons.FiUserCheck,
  officer:             FiIcons.FiUser,
  committee:           FiIcons.FiUsers,
  task_force:          FiIcons.FiUsers,
  group:               FiIcons.FiUsers
};

/**
 * Computes a 5-tier deadline indicator based on the assignment status and dates.
 *
 * Tiers:
 *   1. Completed   -- green check
 *   2. > 14 days   -- green (comfortable)
 *   3. 7-14 days   -- amber (warning)
 *   4. < 7 days    -- red (urgent)
 *   5. Overdue      -- red pulsing
 *   6. Pending      -- slate (no deadline pressure yet)
 *
 * @param {Object} assignment - The assignment object
 * @returns {{ tier, daysRemaining, icon, colorClasses, label }}
 */
const computeDeadlineIndicator = (assignment) => {
  const { status, deadline, completedDate } = assignment;
  const now = new Date();

  // Tier: Completed
  if (status === 'Completed') {
    const completedStr = completedDate
      ? format(new Date(completedDate), 'dd MMM yyyy')
      : 'N/A';
    return {
      tier: 'completed',
      daysRemaining: 0,
      icon: FiIcons.FiCheck,
      colorClasses: 'text-green-600',
      label: `Completed ${completedStr}`
    };
  }

  // Tier: Pending (no deadline set or status is Pending with no active timeline)
  if (status === 'Pending' && !deadline) {
    return {
      tier: 'pending',
      daysRemaining: null,
      icon: FiIcons.FiCircle,
      colorClasses: 'text-slate-500',
      label: 'Pending'
    };
  }

  // Calculate days from now to deadline
  const deadlineDate = new Date(deadline);
  const daysRemaining = differenceInDays(deadlineDate, now);

  // Tier: Overdue (deadline has passed)
  if (daysRemaining < 0) {
    const daysOverdue = Math.abs(daysRemaining);
    return {
      tier: 'overdue',
      daysRemaining,
      daysOverdue,
      icon: FiIcons.FiAlertTriangle,
      colorClasses: 'text-red-600',
      label: `${daysOverdue} day${daysOverdue !== 1 ? 's' : ''} overdue`
    };
  }

  // Tier: < 7 days remaining
  if (daysRemaining < 7) {
    return {
      tier: 'urgent',
      daysRemaining,
      icon: FiIcons.FiAlertCircle,
      colorClasses: 'text-red-600 font-bold',
      label: `${daysRemaining} day${daysRemaining !== 1 ? 's' : ''} remaining`
    };
  }

  // Tier: 7-14 days remaining
  if (daysRemaining <= 14) {
    return {
      tier: 'warning',
      daysRemaining,
      icon: FiIcons.FiClock,
      colorClasses: 'text-amber-600',
      label: `${daysRemaining} days remaining`
    };
  }

  // Tier: > 14 days remaining (comfortable)
  return {
    tier: 'comfortable',
    daysRemaining,
    icon: FiIcons.FiClock,
    colorClasses: 'text-green-600',
    label: `${daysRemaining} days remaining`
  };
};

/**
 * Renders an individual assignment within the DirectiveTree (tree variant)
 * or the flat Assignments tab (table variant).
 *
 * @param {Object} props
 * @param {Object} props.assignment - The assignment data object
 * @param {'tree'|'table'} props.variant - Display variant
 * @param {Function} props.onViewEvidence - Callback when "View Evidence" is clicked
 * @param {Function} props.onSendReminder - Callback when "Send Reminder" is clicked
 * @param {Function} props.onMarkComplete - Callback when "Mark Complete" is clicked
 * @param {Function} props.onEscalate - Callback when "Escalate" is clicked
 */
export const AssignmentRow = ({
  assignment,
  variant = 'tree',
  onViewEvidence,
  onSendReminder,
  onMarkComplete,
  onEscalate
}) => {
  const indicator = useMemo(
    () => computeDeadlineIndicator(assignment),
    [assignment]
  );

  const isOverdue = indicator.tier === 'overdue';
  const isCompleted = indicator.tier === 'completed';

  const assignee = assignment.assignedTo || {};
  const AssigneeIcon = assigneeIcons[assignee.role?.toLowerCase()] || FiIcons.FiUser;
  const evidenceCount = assignment.evidenceAttachments?.length || 0;
  const checklistTotal = assignment.checklistItems?.length || 0;
  const checklistDone = assignment.checklistItems?.filter(c => c.completed).length || 0;

  const formattedDeadline = assignment.deadline
    ? format(new Date(assignment.deadline), 'dd MMM yyyy')
    : 'No deadline';

  const lastUpdateText = assignment.notes || 'No updates submitted';
  const lastUpdatedDate = assignment.lastUpdated
    ? format(new Date(assignment.lastUpdated), 'dd MMM yyyy')
    : null;

  // ---- Tree Variant (card-like, for nested DirectiveTree) ----
  if (variant === 'tree') {
    return (
      <AnimatePresence>
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -8 }}
          transition={{ duration: 0.2 }}
          className={clsx(
            'rounded-lg border p-4 transition-all',
            isOverdue && 'bg-red-50 border-red-200',
            isCompleted && 'bg-green-50/30 border-green-200',
            !isOverdue && !isCompleted && 'bg-white border-border hover:shadow-sm'
          )}
        >
          {/* Top row: assignee info + status badges */}
          <div className="flex items-start justify-between gap-3 mb-3">
            <div className="flex items-start gap-3 flex-1 min-w-0">
              <div className={clsx(
                'p-2 rounded-lg shrink-0',
                isOverdue ? 'bg-red-100 text-red-600' :
                isCompleted ? 'bg-green-100 text-green-600' :
                'bg-blue-100 text-grn-navy'
              )}>
                <SafeIcon icon={AssigneeIcon} className="text-lg" />
              </div>
              <div className="flex-1 min-w-0">
                <h4 className="font-semibold text-sm text-slate-900 truncate">
                  {assignee.name || 'Unassigned'}
                </h4>
                <p className="text-xs text-slate-600 truncate">
                  {assignee.title || assignee.role || ''}
                </p>
                {assignee.directorate && (
                  <p className="text-xs text-slate-500 truncate">
                    {assignee.directorate}
                  </p>
                )}
              </div>
            </div>

            {/* Status badges */}
            <div className="flex items-center gap-2 shrink-0">
              <Badge status={assignment.status} size="sm">
                {assignment.status}
              </Badge>
              {isOverdue && indicator.daysOverdue && (
                <span className="px-2 py-0.5 bg-red-600 text-white text-[10px] font-bold rounded-full animate-pulse whitespace-nowrap">
                  {indicator.daysOverdue} DAY{indicator.daysOverdue !== 1 ? 'S' : ''} OVER
                </span>
              )}
            </div>
          </div>

          {/* Task description (if present) */}
          {assignment.taskDescription && (
            <p className="text-xs text-slate-700 mb-3 line-clamp-2 pl-11">
              {assignment.taskDescription}
            </p>
          )}

          {/* Deadline + last update row */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3 pl-11">
            {/* Deadline indicator */}
            <div className="flex items-center gap-2">
              <SafeIcon
                icon={indicator.icon}
                className={clsx('text-sm', indicator.colorClasses)}
              />
              <div>
                <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">
                  Deadline
                </p>
                <p className={clsx('text-xs font-medium', indicator.colorClasses)}>
                  {formattedDeadline}
                  {isCompleted && assignment.completedDate && (
                    <span className="text-green-600 ml-1">
                      (completed {format(new Date(assignment.completedDate), 'dd MMM')})
                    </span>
                  )}
                  {isOverdue && (
                    <span className="text-red-600 font-bold ml-1">(PASSED)</span>
                  )}
                </p>
              </div>
            </div>

            {/* Last update */}
            <div className="flex items-start gap-2">
              <SafeIcon icon={FiIcons.FiMessageSquare} className="text-sm text-slate-400 mt-0.5" />
              <div>
                <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">
                  Last Update
                </p>
                <p className={clsx(
                  'text-xs line-clamp-2',
                  lastUpdateText === 'No updates submitted'
                    ? 'text-slate-400 italic'
                    : 'text-slate-700'
                )}>
                  {lastUpdateText}
                </p>
              </div>
            </div>
          </div>

          {/* Checklist progress (if items exist) */}
          {checklistTotal > 0 && (
            <div className="mb-3 pl-11">
              <div className="flex items-center gap-2 mb-1">
                <SafeIcon icon={FiIcons.FiCheckSquare} className="text-xs text-slate-400" />
                <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">
                  Checklist
                </span>
                <span className="text-xs text-slate-600 font-medium">
                  {checklistDone}/{checklistTotal}
                </span>
              </div>
              <div className="w-full bg-slate-200 rounded-full h-1.5">
                <div
                  className={clsx(
                    'h-1.5 rounded-full transition-all',
                    checklistDone === checklistTotal ? 'bg-green-500' :
                    checklistDone > 0 ? 'bg-blue-500' :
                    'bg-slate-300'
                  )}
                  style={{ width: `${checklistTotal > 0 ? (checklistDone / checklistTotal) * 100 : 0}%` }}
                />
              </div>
            </div>
          )}

          {/* Action buttons */}
          <div className="flex flex-wrap gap-2 pl-11">
            <Button
              variant="outline"
              size="sm"
              icon={FiIcons.FiPaperclip}
              onClick={() => onViewEvidence?.(assignment)}
            >
              View Evidence ({evidenceCount})
            </Button>

            {!isCompleted && !isOverdue && (
              <Button
                variant="outline"
                size="sm"
                icon={FiIcons.FiCheckCircle}
                onClick={() => onMarkComplete?.(assignment)}
              >
                Mark Complete
              </Button>
            )}

            {!isCompleted && (
              <Button
                variant="ghost"
                size="sm"
                icon={FiIcons.FiBell}
                onClick={() => onSendReminder?.(assignment)}
              >
                Remind
              </Button>
            )}

            {isOverdue && (
              <Button
                variant="destructive"
                size="sm"
                icon={FiIcons.FiAlertTriangle}
                onClick={() => onEscalate?.(assignment)}
              >
                Escalate
              </Button>
            )}
          </div>
        </motion.div>
      </AnimatePresence>
    );
  }

  // ---- Table Variant (row-like, for flat Assignments tab) ----
  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, x: -8 }}
        animate={{ opacity: 1, x: 0 }}
        exit={{ opacity: 0, x: 8 }}
        transition={{ duration: 0.15 }}
        className={clsx(
          'flex items-center gap-4 px-4 py-3 border-b border-border transition-colors',
          isOverdue && 'bg-red-50 border-l-4 border-l-red-500',
          isCompleted && 'bg-green-50/30',
          !isOverdue && !isCompleted && 'hover:bg-slate-50'
        )}
      >
        {/* Assignee icon */}
        <div className={clsx(
          'p-1.5 rounded-md shrink-0',
          isOverdue ? 'bg-red-100 text-red-600' :
          isCompleted ? 'bg-green-100 text-green-600' :
          'bg-slate-100 text-slate-600'
        )}>
          <SafeIcon icon={AssigneeIcon} className="text-sm" />
        </div>

        {/* Assignee name + role */}
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-slate-900 truncate">
            {assignee.name || 'Unassigned'}
          </p>
          <p className="text-xs text-slate-500 truncate">
            {assignee.title || assignee.role || ''}
            {assignee.ministry && ` \u2014 ${assignee.ministry}`}
          </p>
        </div>

        {/* Task description (truncated) */}
        <div className="hidden md:block flex-1 min-w-0">
          <p className="text-xs text-slate-600 truncate">
            {assignment.taskDescription || '\u2014'}
          </p>
        </div>

        {/* Deadline indicator */}
        <div className="flex items-center gap-1.5 shrink-0 min-w-[140px]">
          <SafeIcon
            icon={indicator.icon}
            className={clsx('text-xs', indicator.colorClasses)}
          />
          <span className={clsx('text-xs whitespace-nowrap', indicator.colorClasses)}>
            {formattedDeadline}
          </span>
        </div>

        {/* Progress */}
        <div className="hidden lg:flex items-center gap-2 shrink-0 min-w-[80px]">
          <div className="w-12 bg-slate-200 rounded-full h-1.5">
            <div
              className={clsx(
                'h-1.5 rounded-full',
                assignment.progress >= 100 ? 'bg-green-500' :
                assignment.progress >= 50 ? 'bg-blue-500' :
                assignment.progress > 0 ? 'bg-amber-500' :
                'bg-slate-300'
              )}
              style={{ width: `${Math.min(assignment.progress || 0, 100)}%` }}
            />
          </div>
          <span className="text-xs text-slate-600 font-medium">
            {assignment.progress || 0}%
          </span>
        </div>

        {/* Status badge */}
        <div className="flex items-center gap-2 shrink-0">
          <Badge status={assignment.status} size="sm">
            {assignment.status}
          </Badge>
          {isOverdue && indicator.daysOverdue && (
            <span className="px-1.5 py-0.5 bg-red-600 text-white text-[10px] font-bold rounded-full animate-pulse whitespace-nowrap">
              +{indicator.daysOverdue}d
            </span>
          )}
        </div>

        {/* Row actions */}
        <div className="flex items-center gap-1 shrink-0">
          <button
            onClick={() => onViewEvidence?.(assignment)}
            className="p-1.5 rounded-md text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors"
            title={`View Evidence (${evidenceCount})`}
          >
            <SafeIcon icon={FiIcons.FiPaperclip} className="text-sm" />
          </button>

          {!isCompleted && (
            <button
              onClick={() => onSendReminder?.(assignment)}
              className="p-1.5 rounded-md text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors"
              title="Send Reminder"
            >
              <SafeIcon icon={FiIcons.FiBell} className="text-sm" />
            </button>
          )}

          {!isCompleted && !isOverdue && (
            <button
              onClick={() => onMarkComplete?.(assignment)}
              className="p-1.5 rounded-md text-slate-400 hover:text-green-600 hover:bg-green-50 transition-colors"
              title="Mark Complete"
            >
              <SafeIcon icon={FiIcons.FiCheckCircle} className="text-sm" />
            </button>
          )}

          {isOverdue && (
            <button
              onClick={() => onEscalate?.(assignment)}
              className="p-1.5 rounded-md text-red-400 hover:text-red-600 hover:bg-red-50 transition-colors"
              title="Escalate"
            >
              <SafeIcon icon={FiIcons.FiAlertTriangle} className="text-sm" />
            </button>
          )}
        </div>
      </motion.div>
    </AnimatePresence>
  );
};
